import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import { trackEvent } from '../utils/analytics';

const Settings = () => {
  const { user } = useAuth();
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [editingExchange, setEditingExchange] = useState(null);
  const [showApiKey, setShowApiKey] = useState({});
  const [showApiSecret, setShowApiSecret] = useState({});

  useEffect(() => {
    document.title = 'Settings';
  }, []);

  useEffect(() => {
    if (user) {
      fetchExchangesWithApiKeys();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchExchangesWithApiKeys = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/api-keys/${user.sub}/exchanges`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setExchanges(data.data);
      } else {
        throw new Error(data.error || 'Failed to fetch exchanges');
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching exchanges:', err);
      setError('Failed to fetch exchanges data');
      setExchanges([]);
      setLoading(false);
    }
  };

  const handleEdit = (exchange) => {
    setEditingExchange({
      exchangeId: exchange.exchangeId,
      exchangeName: exchange.exchangeName,
      apiKey: exchange.apiKey || '',
      apiSecret: exchange.apiSecret || '',
      apiPassphrase: exchange.apiPassphrase || '',
      label: exchange.label || '',
      apiKeyExpiresAt: exchange.apiKeyExpiresAt ? exchange.apiKeyExpiresAt.split('T')[0] : '',
      apiSecretExpiresAt: exchange.apiSecretExpiresAt ? exchange.apiSecretExpiresAt.split('T')[0] : ''
    });
    setSuccessMessage('');
    setError(null);

    // Track edit dialog opened
    trackEvent('Settings', exchange.hasApiKey ? 'Edit API Key Dialog Opened' : 'Add API Key Dialog Opened', exchange.exchangeName);
  };

  const handleCancel = () => {
    setEditingExchange(null);
    setSuccessMessage('');
    setError(null);
  };

  const handleSave = async () => {
    if (!editingExchange.apiKey || !editingExchange.apiSecret) {
      setError('API Key and API Secret are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/api-keys/${user.sub}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          exchangeId: editingExchange.exchangeId,
          apiKey: editingExchange.apiKey,
          apiSecret: editingExchange.apiSecret,
          apiPassphrase: editingExchange.apiPassphrase || null,
          label: editingExchange.label || null,
          apiKeyExpiresAt: editingExchange.apiKeyExpiresAt || null,
          apiSecretExpiresAt: editingExchange.apiSecretExpiresAt || null
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setSuccessMessage(`API key for ${editingExchange.exchangeName} saved successfully!`);

        // Track successful API key save
        trackEvent('Settings', 'API Key Saved', editingExchange.exchangeName);

        setEditingExchange(null);
        await fetchExchangesWithApiKeys();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error(data.error || 'Failed to save API key');
      }

      setSaving(false);
    } catch (err) {
      console.error('Error saving API key:', err);
      setError('Failed to save API key: ' + err.message);

      // Track API key save failure
      trackEvent('Settings', 'API Key Save Failed', editingExchange.exchangeName);

      setSaving(false);
    }
  };

  const handleDelete = async (exchangeId, exchangeName) => {
    if (!window.confirm(`Are you sure you want to delete the API key for ${exchangeName}?`)) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/api-keys/${user.sub}/exchange/${exchangeId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setSuccessMessage(`API key for ${exchangeName} deleted successfully!`);

        // Track successful API key deletion
        trackEvent('Settings', 'API Key Deleted', exchangeName);

        await fetchExchangesWithApiKeys();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error(data.error || 'Failed to delete API key');
      }

      setSaving(false);
    } catch (err) {
      console.error('Error deleting API key:', err);
      setError('Failed to delete API key: ' + err.message);

      // Track API key deletion failure
      trackEvent('Settings', 'API Key Delete Failed', exchangeName);

      setSaving(false);
    }
  };

  const toggleShowApiKey = (exchangeId) => {
    const willShow = !showApiKey[exchangeId];
    setShowApiKey(prev => ({
      ...prev,
      [exchangeId]: willShow
    }));

    // Track API key visibility toggle
    const exchange = exchanges.find(ex => ex.exchangeId === exchangeId);
    if (exchange) {
      trackEvent('Settings', willShow ? 'API Key Shown' : 'API Key Hidden', exchange.exchangeName);
    }
  };

  const toggleShowApiSecret = (exchangeId) => {
    const willShow = !showApiSecret[exchangeId];
    setShowApiSecret(prev => ({
      ...prev,
      [exchangeId]: willShow
    }));

    // Track API secret visibility toggle
    const exchange = exchanges.find(ex => ex.exchangeId === exchangeId);
    if (exchange) {
      trackEvent('Settings', willShow ? 'API Secret Shown' : 'API Secret Hidden', exchange.exchangeName);
    }
  };

  const maskValue = (value) => {
    if (!value || value.length <= 8) return '********';
    return value.substring(0, 4) + '********' + value.substring(value.length - 4);
  };

  const formatDateDDMMYYYY = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  if (loading) {
    return (
      <div className="page-content">
        <div style={{ color: '#8b949e', padding: '20px' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="page-content">
      {error && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          backgroundColor: '#f85149',
          color: '#ffffff',
          borderRadius: '4px',
          fontSize: '12px'
        }}>
          {error}
        </div>
      )}

      {successMessage && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          backgroundColor: '#3fb950',
          color: '#ffffff',
          borderRadius: '4px',
          fontSize: '12px'
        }}>
          {successMessage}
        </div>
      )}

      {editingExchange && (
        <>
          {/* Modal Overlay */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}
            onClick={handleCancel}
          >
            {/* Modal Content */}
            <div
              className="data-table"
              style={{
                padding: '30px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '90vh',
                overflowY: 'auto',
                position: 'relative'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ margin: '0 0 20px 0', color: '#ffffff', fontSize: '18px' }}>
                {editingExchange.apiKey ? 'Edit' : 'Add'} API Key - {editingExchange.exchangeName}
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', color: '#8b949e', fontSize: '11px', marginBottom: '5px' }}>
                Label (Optional)
              </label>
              <input
                type="text"
                value={editingExchange.label}
                onChange={(e) => setEditingExchange({ ...editingExchange, label: e.target.value })}
                placeholder="e.g., Main Account"
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '12px',
                  backgroundColor: '#21262d',
                  border: '1px solid #2a2d3a',
                  borderRadius: '4px',
                  color: '#ffffff'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', color: '#8b949e', fontSize: '11px', marginBottom: '5px' }}>
                API Key *
              </label>
              <input
                type="text"
                value={editingExchange.apiKey}
                onChange={(e) => setEditingExchange({ ...editingExchange, apiKey: e.target.value })}
                placeholder="Enter API Key"
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '12px',
                  backgroundColor: '#21262d',
                  border: '1px solid #2a2d3a',
                  borderRadius: '4px',
                  color: '#ffffff'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', color: '#8b949e', fontSize: '11px', marginBottom: '5px' }}>
                API Secret *
              </label>
              <input
                type="password"
                value={editingExchange.apiSecret}
                onChange={(e) => setEditingExchange({ ...editingExchange, apiSecret: e.target.value })}
                placeholder="Enter API Secret"
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '12px',
                  backgroundColor: '#21262d',
                  border: '1px solid #2a2d3a',
                  borderRadius: '4px',
                  color: '#ffffff'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', color: '#8b949e', fontSize: '11px', marginBottom: '5px' }}>
                API Passphrase (Optional)
              </label>
              <input
                type="password"
                value={editingExchange.apiPassphrase}
                onChange={(e) => setEditingExchange({ ...editingExchange, apiPassphrase: e.target.value })}
                placeholder="Required by some exchanges"
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '12px',
                  backgroundColor: '#21262d',
                  border: '1px solid #2a2d3a',
                  borderRadius: '4px',
                  color: '#ffffff'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <label style={{ display: 'block', color: '#8b949e', fontSize: '11px', marginBottom: '5px' }}>
                  API Key Expires At (Optional)
                </label>
                <input
                  type="date"
                  value={editingExchange.apiKeyExpiresAt}
                  onChange={(e) => setEditingExchange({ ...editingExchange, apiKeyExpiresAt: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px',
                    fontSize: '12px',
                    backgroundColor: '#21262d',
                    border: '1px solid #2a2d3a',
                    borderRadius: '4px',
                    color: '#ffffff'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#8b949e', fontSize: '11px', marginBottom: '5px' }}>
                  API Secret Expires At (Optional)
                </label>
                <input
                  type="date"
                  value={editingExchange.apiSecretExpiresAt}
                  onChange={(e) => setEditingExchange({ ...editingExchange, apiSecretExpiresAt: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px',
                    fontSize: '12px',
                    backgroundColor: '#21262d',
                    border: '1px solid #2a2d3a',
                    borderRadius: '4px',
                    color: '#ffffff'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  fontSize: '12px',
                  backgroundColor: '#000000',
                  border: '1px solid #3fb950',
                  borderRadius: '0',
                  color: '#3fb950',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight: '500'
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancel}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  fontSize: '12px',
                  backgroundColor: '#000000',
                  border: '1px solid #8b949e',
                  borderRadius: '0',
                  color: '#8b949e',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              </div>
            </div>
            </div>
          </div>
        </>
      )}

      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th style={{ fontSize: '11px' }}>Exchange</th>
              <th style={{ fontSize: '11px' }}>Status</th>
              <th style={{ fontSize: '11px' }}>Label</th>
              <th style={{ fontSize: '11px' }}>API Key</th>
              <th style={{ fontSize: '11px' }}>API Secret</th>
              <th style={{ fontSize: '11px' }}>Passphrase</th>
              <th style={{ fontSize: '11px' }}>Key Expires</th>
              <th style={{ fontSize: '11px' }}>Secret Expires</th>
              <th style={{ fontSize: '11px' }}>Last Updated</th>
              <th style={{ fontSize: '11px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {exchanges.map((exchange) => (
              <tr key={exchange.exchangeId}>
                <td style={{ fontSize: '11px' }}>{exchange.exchangeName}</td>
                <td style={{
                  fontSize: '11px',
                  color: exchange.exchangeStatus === 'ACTIVE' ? '#3fb950' : '#f85149'
                }}>
                  {exchange.exchangeStatus}
                </td>
                <td style={{ color: '#8b949e', fontSize: '11px' }}>
                  {exchange.label || '-'}
                </td>
                <td>
                  {exchange.hasApiKey ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span style={{ fontSize: '11px', fontFamily: 'monospace' }}>
                        {showApiKey[exchange.exchangeId] ? exchange.apiKey : maskValue(exchange.apiKey)}
                      </span>
                      <button
                        onClick={() => toggleShowApiKey(exchange.exchangeId)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#3fb950',
                          cursor: 'pointer',
                          fontSize: '10px',
                          padding: '2px 5px'
                        }}
                      >
                        {showApiKey[exchange.exchangeId] ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  ) : (
                    <span style={{ color: '#6e7681', fontSize: '11px' }}>Not set</span>
                  )}
                </td>
                <td>
                  {exchange.hasApiKey ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span style={{ fontSize: '11px', fontFamily: 'monospace' }}>
                        {showApiSecret[exchange.exchangeId] ? exchange.apiSecret : maskValue(exchange.apiSecret)}
                      </span>
                      <button
                        onClick={() => toggleShowApiSecret(exchange.exchangeId)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#3fb950',
                          cursor: 'pointer',
                          fontSize: '10px',
                          padding: '2px 5px'
                        }}
                      >
                        {showApiSecret[exchange.exchangeId] ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  ) : (
                    <span style={{ color: '#6e7681', fontSize: '11px' }}>Not set</span>
                  )}
                </td>
                <td style={{ fontSize: '11px', color: '#8b949e' }}>
                  {exchange.apiPassphrase ? '••••••••' : '-'}
                </td>
                <td style={{ fontSize: '11px', color: exchange.apiKeyExpiresAt && new Date(exchange.apiKeyExpiresAt) < new Date() ? '#f85149' : '#8b949e' }}>
                  {formatDateDDMMYYYY(exchange.apiKeyExpiresAt)}
                </td>
                <td style={{ fontSize: '11px', color: exchange.apiSecretExpiresAt && new Date(exchange.apiSecretExpiresAt) < new Date() ? '#f85149' : '#8b949e' }}>
                  {formatDateDDMMYYYY(exchange.apiSecretExpiresAt)}
                </td>
                <td style={{ fontSize: '11px', color: '#8b949e' }}>
                  {formatDateDDMMYYYY(exchange.updatedAt)}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button
                      onClick={() => handleEdit(exchange)}
                      disabled={saving}
                      style={{
                        padding: '4px 10px',
                        fontSize: '11px',
                        backgroundColor: '#000000',
                        border: '1px solid #3fb950',
                        borderRadius: '0',
                        color: '#3fb950',
                        cursor: saving ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {exchange.hasApiKey ? 'Edit' : 'Add'}
                    </button>
                    {exchange.hasApiKey && (
                      <button
                        onClick={() => handleDelete(exchange.exchangeId, exchange.exchangeName)}
                        disabled={saving}
                        style={{
                          padding: '4px 10px',
                          fontSize: '11px',
                          backgroundColor: '#000000',
                          border: '1px solid #f85149',
                          borderRadius: '0',
                          color: '#f85149',
                          cursor: saving ? 'not-allowed' : 'pointer'
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{
        marginTop: '20px',
        padding: '15px',
        backgroundColor: '#1a1b23',
        border: '1px solid #2a2d3a',
        borderRadius: '4px',
        fontSize: '11px',
        color: '#8b949e'
      }}>
        <strong style={{ color: '#ffffff' }}>Security Note:</strong> Your API keys are encrypted using AES-256-GCM encryption
        before being stored in the database. They are only decrypted when displayed to you.
      </div>
    </div>
  );
};

export default Settings;
